<template>
  <div class="row">
    <div class="medium-6 small-12 columns">
      <section>
        <h1 v-html="stepHeadline" />

        <!-- Hvilken genstand er blevet stjålet eller beskadiget -->
        <article class="flow-group">
          <h3 v-html="equipmentNameHeadline" />

          <div class="control push-down">
            <p v-html="equipmentNameLabel" />
            <Field
              v-slot="{ field }"
              v-model="equipmentName"
              :rules="{ required: equipment.length < 1 }"
              :label="equipmentNameLabel"
              name="bike-theft-claim-flow-equipment-name"
            >
              <vue-select
                v-bind="field"
                :options="equipmentNameOptionsDecoded"
                :placeholder="equipmentNameLabel"
              />
              <ValidationError name="bike-theft-claim-flow-equipment-name" />
            </Field>
          </div>
          <div class="control push-down">
            <div v-if="otherEquipmentNameOptionSelected">
              <p v-html="equipmentOptionalNameLabel" />
              <Field
                v-slot="{ field }"
                v-model="optionalEquipmentName"
                :rules="'required'"
                :label="equipmentOptionalNameLabel"
                name="bike-theft-claim-flow-equipment-optional-name"
              >
                <base-text-input v-bind="field" maxlength="50" />
                <ValidationError name="bike-theft-claim-flow-equipment-optional-name" />
              </Field>
            </div>
          </div>

          <div class="control">
            <div class="row">
              <div class="columns medium-6 small-6">
                <h3 v-html="equipmentAgeHeadline" />
              </div>
              <div class="columns medium-6 small-6">
                <h3 v-html="equipmentPriceHeadline" />
              </div>
              <div class="columns medium-6 small-6">
                <p v-html="equipmentAgeLabel" />
                <Field
                  v-slot="{ field }"
                  v-model="equipmentAge"
                  :rules="{
                    required:
                      (!otherEquipmentNameOptionSelected && equipmentName !== null) ||
                      (otherEquipmentNameOptionSelected && optionalEquipmentName !== null)
                  }"
                  :label="equipmentAgeHeadline"
                  name="bike-theft-claim-flow-equipment-age"
                >
                  <vue-select
                    v-bind="field"
                    :disabled="
                      (!otherEquipmentNameOptionSelected && equipmentName === null) ||
                      (otherEquipmentNameOptionSelected && optionalEquipmentName === null)
                    "
                    :options="equipmentAgeOptions"
                    :placeholder="equipmentAgeLabel"
                  />
                  <ValidationError name="bike-theft-claim-flow-equipment-age" />
                </Field>
              </div>
              <div class="columns medium-6 small-6">
                <p v-html="equipmentPriceLabel" />
                <Field
                  v-slot="{ field }"
                  v-model="equipmentPrice"
                  :label="equipmentPriceHeadline"
                  name="bike-theft-claim-flow-equipment-price"
                  :rules="{
                    required:
                      (!otherEquipmentNameOptionSelected && equipmentName !== null) ||
                      (otherEquipmentNameOptionSelected && optionalEquipmentName !== null),
                    numeric: true
                  }"
                >
                  <base-text-input
                    v-bind="field"
                    :disabled="
                      (!otherEquipmentNameOptionSelected && equipmentName === null) ||
                      (otherEquipmentNameOptionSelected && optionalEquipmentName === null)
                    "
                    maxlength="5"
                  />
                  <ValidationError name="bike-theft-claim-flow-equipment-price" />
                </Field>
              </div>
            </div>

            <div class="row">
              <div class="columns small-3 small-offset-9">
                <button
                  type="button"
                  class="button text-center"
                  :disabled="!enableAddButton"
                  name="bike-theft-claim-flow-add-equipment-item-button"
                  @click="addEquipmentItem"
                  v-html="saveAndContinueButtonText"
                />
              </div>
            </div>
          </div>
        </article>

        <div class="row">
          <div class="columns medium-6">
            <router-link
              class="button secondary text-center"
              to="/opret-cykel-skade/what/bike"
              data-test-bike-theft-claim-flow-link-button-back
            >
              Tilbage
            </router-link>
          </div>
          <div class="columns medium-6">
            <router-link
              class="button text-center"
              to="/opret-cykel-skade/what/bike/equipment/summary"
              data-test-bike-theft-claim-flow-link-button-next
            >
              Videre
            </router-link>
          </div>
        </div>
      </section>
    </div>

    <div class="medium-6 small-12 columns">
      <equipment :class="{ hide: !showEquipmentList }" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import BaseTextInput from '@/components/Forms/BaseTextInput.vue';
import Utility from '@/common/UtilityFunctions.js';
import BeforeRouteLeaveMixin from '../Mixins/beforeRouteLeave';
import MediaQueriesMixin from '@/components/Mixins/MediaQueriesMixin';
import Equipment from '../components/Equipment.vue';
import { isEmpty } from 'lodash';

export default {
  name: 'ExtraEquipment',
  components: {
    BaseTextInput,
    Equipment
  },
  mixins: [BeforeRouteLeaveMixin, MediaQueriesMixin],
  beforeRouteLeave(to, from, next) {
    this.leaveRoute(to, from, next);
  },
  props: {
    errors: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  data() {
    return {
      equipmentAge: null,
      equipmentName: null,
      equipmentPrice: null,
      optionalEquipmentName: null
    };
  },
  computed: {
    ...mapState({
      equipment: (state) =>
        state.bikeTheftClaimFlow.data.submitData.bicycleClaimQuestionsTemplateModel.equipment,
      equipmentAgeHeadline: (state) =>
        state.bikeTheftClaimFlow.data.equipmentPage.equipmentAgeHeadline,
      equipmentAgeLabel: (state) =>
        Utility.decodeHtml(state.bikeTheftClaimFlow.data.equipmentPage.equipmentAgeLabel),
      equipmentAgeOptions: (state) =>
        state.bikeTheftClaimFlow.data.equipmentPage.equipmentAgeOptions,
      equipmentNameHeadline: (state) =>
        state.bikeTheftClaimFlow.data.equipmentPage.equipmentNameHeadline,
      equipmentNameLabel: (state) =>
        Utility.decodeHtml(state.bikeTheftClaimFlow.data.equipmentPage.equipmentNameLabel),
      equipmentNameOptions: (state) =>
        state.bikeTheftClaimFlow.data.equipmentPage.equipmentNameOptions,
      equipmentPriceHeadline: (state) =>
        state.bikeTheftClaimFlow.data.equipmentPage.equipmentPriceHeadline,
      equipmentPriceLabel: (state) =>
        state.bikeTheftClaimFlow.data.equipmentPage.equipmentPriceLabel,
      equipmentOptionalNameLabel: (state) =>
        state.bikeTheftClaimFlow.data.equipmentPage.equipmentOptionalNameLabel,
      saveAndContinueButtonText: (state) =>
        state.bikeTheftClaimFlow.data.equipmentPage.saveAndContinueButtonText,
      stepHeadline: (state) => state.bikeTheftClaimFlow.data.equipmentPage.stepHeadline
    }),

    enableAddButton() {
      return this.equipmentAge !== null && this.equipmentPrice !== null && isEmpty(this.errors);
    },
    equipmentNameOptionsDecoded() {
      return this.equipmentNameOptions.map((obj) => {
        obj.label = Utility.decodeHtml(obj.label);
        obj.value = Utility.decodeHtml(obj.value);
        return obj;
      });
    },
    otherEquipmentNameOptionSelected() {
      return this.equipmentName !== null && this.equipmentName.value === 'Andet';
    },
    showEquipmentList() {
      return this.equipment.length > 0;
    }
  },
  watch: {
    equipmentName() {
      this.equipmentAge = null;
      this.equipmentPrice = null;
      this.optionalEquipmentName = null;
      this.$parent.$parent.$parent.setErrors({});
    },
    optionalEquipmentName(val) {
      if (val === '') {
        this.equipmentAge = null;
        this.equipmentPrice = null;
        this.optionalEquipmentName = null;
        this.$parent.$parent.$parent.setErrors({});
      }
    }
  },
  methods: {
    addEquipmentItem() {
      this.$store.dispatch('bikeTheftClaimFlow/setEquipment', {
        name:
          this.optionalEquipmentName !== null
            ? this.optionalEquipmentName
            : this.equipmentName.value,
        age: this.equipmentAge.value,
        price: this.equipmentPrice
      });

      this.resetEquipmentFields();

      if (this.mediaQueries.isSmallOnly) {
        this.$nextTick(() => {
          Utility.scrollToElem('#bike-theft-claim-flow-equipment');
        });
      }
    },
    resetEquipmentFields() {
      this.equipmentAge = null;
      this.equipmentName = null;
      this.equipmentPrice = null;
      this.optionalEquipmentName = null;
    }
  }
};
</script>
